import { useEffect, useLayoutEffect, useState } from "react";
import { useForm, Controller, FieldError, FormProvider } from "react-hook-form";
import { observer } from "mobx-react-lite";

import { AddressDetails } from "../../../../core/api/addresses/types";
import { Row, Spacer, TwoColGrid } from "../../../../components/Layout/Layout";
import {
  validateEmergencyPhoneNumberIfExists,
  validatePhoneNumberIfExists,
} from "../../../../core/helpers/validation";
import { notifyApiErrors } from "../../../../core/helpers/helpers";
import { getAddressesAutocomplete } from "../../../../core/api/autocomplite/autocomplite";

import TextInput from "../../../../ui-lib/components/Inputs/TextInput";
import TextArea from "../../../../ui-lib/components/Inputs/Textarea";
import useTranslations from "../../../../core/i18n/useTranslations";
import MarkerIcon from "../../../../ui-lib/assets/icons/marker.svg";
import usePageState from "../../../../core/pagestate/usePageState";
import useUser from "../../../../core/user/useUser";
import Alert from "../../../../ui-lib/components/Alerts/Alert";
import PrimeModal from "../../../../ui-lib/components/PrimeModal/PrimeModal";
import LoadingSpinner from "../../../../ui-lib/components/Loading/LoadingSpinner";
import GooglePlaceAutocomplete from "../../../../components/GooglePlaceAutocomplete";
import Autocomplete from "../../../../ui-lib/components/Autocomplete/Autocomplete";

const BASE_PLACEMARK_URL = "https://www.google.com/maps/search/?api=1&query=";

const AddressForm = ({
  objectId,
  isOpen,
  labels,
  defaultValues,
  isLoading,
  onSubmit,
  onClose,
}: {
  objectId: string;
  isOpen: boolean;
  labels: {
    title: string;
    description: string;
    submit: string;
  };
  defaultValues?: AddressDetails;
  isLoading?: boolean;
  onSubmit: (values: AddressDetails) => void;
  onClose: () => void;
}) => {
  const t = useTranslations();
  const pageState = usePageState();
  const { config, googleApiKey, authenticatedRequest } = useUser();

  const [inputAddress, setInputAddress] = useState(
    defaultValues?.streetAddress ?? ""
  );
  const [autocompleteData, setAutocompleteData] =
    useState<Record<string, any>>();

  const methods = useForm<AddressDetails>({
    defaultValues,
  });
  const {
    control,
    formState: { isSubmitting, errors, isDirty },
    handleSubmit,
    getValues,
    setValue,
    watch,
    trigger,
    reset,
  } = methods;

  const fetchAutocompleteData = async () => {
    try {
      const res = await getAddressesAutocomplete(
        objectId,
        authenticatedRequest
      );
      setAutocompleteData(res.data.fields);
    } catch (error: any) {
      notifyApiErrors(error.response?.data?.errors);
    }
  };

  const getComponent = (
    components: google.maps.GeocoderAddressComponent[],
    type: string
  ) => components.find((c) => c.types.includes(type));

  const handleAddressPlaceSelected = ({ place }: { place: any }) => {
    const components = place?.address_components ?? [];
    const streetAddress = getComponent(components, "route");
    const streetNumber = getComponent(components, "street_number");
    const premise = getComponent(components, "premise");
    const locality = getComponent(components, "locality");
    const postalTown = getComponent(components, "postal_town");
    const postalCode = getComponent(components, "postal_code");
    const country = getComponent(components, "country");

    setValue("city", postalTown?.long_name ?? locality?.long_name ?? "", {
      shouldDirty: true,
    });
    setValue("country", country?.long_name ?? "", {
      shouldDirty: true,
    });
    setValue("postalCode", postalCode?.long_name ?? "", {
      shouldDirty: true,
    });

    const location = place?.geometry?.location;
    setValue("latitude", location?.lat?.(), {
      shouldDirty: true,
    });
    setValue("longitude", location?.lng?.(), {
      shouldDirty: true,
    });

    const isUK = country?.short_name === "GB" || country?.short_name === "UK";
    const addressParts: string[] = [];

    if (isUK) {
      if (streetNumber?.long_name) addressParts.push(streetNumber.long_name);
      if (premise?.long_name) addressParts.push(premise.long_name);
      if (streetAddress?.long_name) addressParts.push(streetAddress.long_name);
      if (locality?.long_name) addressParts.push(locality.long_name);
      setInputAddress(addressParts.join(", "));
    } else if (streetAddress) {
      setInputAddress(
        streetNumber?.long_name
          ? `${streetAddress.long_name} ${streetNumber.long_name}`
          : streetAddress.long_name
      );
    } else if (place?.name) {
      setInputAddress(place.name);
    }
    trigger();
  };

  useEffect(() => {
    setValue("streetAddress", inputAddress, {
      shouldDirty: true,
    });
  }, [inputAddress]);

  const getPlacemarkLink = () =>
    `${BASE_PLACEMARK_URL}${getValues("latitude")},${getValues("longitude")}`;

  const getErrorMessage = (error?: FieldError) => {
    if (error === undefined) return "";

    if (error.type === "required") {
      return t("Objects:address_input_required_fields");
    }

    if (
      error.type === "validateTelephone" ||
      error.type === "validateOptionalTelephone" ||
      error.type === "validate"
    )
      return t("Errors:response_invalid_telephone_number_format");

    return "Undefined Error";
  };

  useLayoutEffect(() => {
    if (defaultValues) {
      reset(defaultValues);
    }
  }, [defaultValues]);

  useEffect(() => {
    fetchAutocompleteData();
  }, []);

  return (
    <PrimeModal
      withFooter
      withHeader
      isOpen={isOpen}
      onClose={onClose}
      fixedWidth="848px"
      header={labels.title}
      loading={isSubmitting}
      submitBtn={{
        text: labels.submit,
        onClick: handleSubmit(onSubmit),
        disabled: !isDirty,
      }}
      cancelBtn={{
        text: t("Common:cancel"),
        onClick: onClose,
      }}
    >
      <p className="grey-600">{labels.description}</p>

      <Spacer size={32} />

      {isLoading ? (
        <LoadingSpinner />
      ) : (
        <FormProvider {...methods}>
          <form onSubmit={(e) => e.preventDefault()} id="address-modal-form">
            <Row align="start">
              <Controller
                name="streetAddress"
                control={control}
                rules={{ required: !watch("telephone1") }}
                render={({ field }) => (
                  <GooglePlaceAutocomplete
                    label={t("Objects:address_input_street")}
                    placeholder={t("Common:street_placeholder")}
                    value={field.value}
                    onChange={(e) => {
                      field.onChange(e);
                      trigger("telephone1");
                    }}
                    onValueChange={(val) => setInputAddress(val)}
                    onPlaceSelected={handleAddressPlaceSelected}
                    apiKey={googleApiKey?.api_key}
                    language={
                      pageState.rootStore.languageStore.currentLanguage
                        .nativeName
                    }
                  />
                )}
              />
              <Spacer size={32} />
              <Controller
                name="apartmentNumber"
                control={control}
                rules={{ maxLength: 100 }}
                render={({ field }) => (
                  <TextInput
                    inputRef={field.ref}
                    label={t("Common:apartment_number")}
                    value={field.value}
                    onChange={field.onChange}
                    placeholder={t("Common:apartment_placeholder")}
                    validationError={
                      errors?.apartmentNumber &&
                      t("Errors:max_chars_exceeded").replace("{0}", "100")
                    }
                  />
                )}
              />
            </Row>

            <Spacer size={16} />

            <Row align="start">
              <Controller
                name="city"
                control={control}
                render={({ field }) => (
                  <TextInput
                    label={t("Common:labels_city")}
                    value={field.value}
                    onChange={field.onChange}
                    placeholder={t("Common:city_placeholder")}
                  />
                )}
              />
              <Spacer size={32} />
              <Controller
                name="postalCode"
                rules={{ maxLength: 10 }}
                control={control}
                render={({ field }) => (
                  <TextInput
                    inputRef={field.ref}
                    label={t("Common:labels_postal_code")}
                    value={field.value}
                    onChange={field.onChange}
                    placeholder={t("Common:postal_code_placeholder")}
                    validationError={
                      errors?.postalCode &&
                      t("Errors:max_chars_exceeded").replace("{0}", "10")
                    }
                  />
                )}
              />
            </Row>

            <Spacer size={16} />

            <Row align="start">
              <Controller
                name="country"
                control={control}
                render={({ field }) => (
                  <TextInput
                    label={t("Common:labels_country")}
                    value={field.value}
                    onChange={field.onChange}
                    placeholder={t("Common:country_placeholder")}
                  />
                )}
              />
            </Row>

            <Spacer size={16} />

            <Row align="start">
              <Controller
                name="customerSpecific1"
                control={control}
                rules={{ maxLength: 200 }}
                render={({ field }) => (
                  <TextInput
                    inputRef={field.ref}
                    label={t("Common:customer_specific_1")}
                    value={field.value}
                    onChange={field.onChange}
                    validationError={
                      errors?.customerSpecific1 &&
                      t("Errors:max_chars_exceeded").replace("{0}", "200")
                    }
                  />
                )}
              />
              <Spacer size={32} />
              <Controller
                name="customerSpecific2"
                control={control}
                rules={{ maxLength: 200 }}
                render={({ field }) => (
                  <TextInput
                    inputRef={field.ref}
                    label={t("Common:customer_specific_2")}
                    value={field.value}
                    onChange={field.onChange}
                    validationError={
                      errors?.customerSpecific2 &&
                      t("Errors:max_chars_exceeded").replace("{0}", "200")
                    }
                  />
                )}
              />
            </Row>

            <Spacer size={16} />
            <TwoColGrid>
              <Controller
                name="propertyReference"
                control={control}
                rules={{ maxLength: 24 }}
                render={({ field }) => (
                  <TextInput
                    inputRef={field.ref}
                    label={t("Objects:UPRN_label")}
                    placeholder={t("Objects:UPRN_placeholder")}
                    value={field.value}
                    onChange={field.onChange}
                    validationError={
                      errors?.propertyReference &&
                      t("Errors:max_chars_exceeded").replace("{0}", "24")
                    }
                  />
                )}
              />
            </TwoColGrid>
            <Spacer size={16} />

            <Controller
              name="description"
              control={control}
              rules={{ maxLength: 150 }}
              render={({ field }) => (
                <TextArea
                  inputRef={field.ref}
                  label={t("Objects:address_input_description")}
                  placeholder={t("Errors:MaximumLengthExceeded").replace(
                    "{0}",
                    "150"
                  )}
                  value={field.value}
                  onChange={field.onChange}
                  validationError={
                    errors.description &&
                    t("Errors:length_to_long").replace("{0}", "150")
                  }
                />
              )}
            />

            <Spacer size={8} />

            <Row align="start">
              <Controller
                name="latitude"
                control={control}
                rules={{
                  max: 90,
                  min: -90,
                  pattern: /^(-?\d{1,2}([.]?\d+)?)$/,
                }}
                render={({ field }) => (
                  <TextInput
                    inputRef={field.ref}
                    label={t("Common:labels_latitude")}
                    value={`${field.value ?? ""}`.substring(0, 10)}
                    onChange={field.onChange}
                    validationError={
                      errors?.latitude && t("Errors:ValidationHint_Latitude")
                    }
                  />
                )}
              />
              <Spacer size={32} />
              <Controller
                name="longitude"
                control={control}
                rules={{
                  max: 180,
                  min: -180,
                  pattern: /^(-?\d{1,3}([.]?\d+)?)$/,
                }}
                render={({ field }) => (
                  <TextInput
                    inputRef={field.ref}
                    label={t("Common:labels_longitude")}
                    value={`${field.value ?? ""}`.substring(0, 10)}
                    onChange={field.onChange}
                    validationError={
                      errors?.longitude && t("Errors:ValidationHint_Longitude")
                    }
                  />
                )}
              />
            </Row>
            <Spacer size={16} />
            <Alert
              withDefaultIcon
              title={t("Objects:custom_address_description")}
              variant="info"
            />
            <Spacer size={16} />
            <Controller
              name="what3Words"
              control={control}
              rules={{
                maxLength: 100,
                validate: (value) => {
                  if (!value) return true;
                  const inputFormat = /^(\/\/\/)?[\p{L}_]+(\.[\p{L}_]+){2}$/u;
                  return inputFormat.test(value ?? "");
                },
              }}
              render={({ field }) => (
                <TextInput
                  inputRef={field.ref}
                  label={t("Objects:what3words_label")}
                  placeholder={t("Errors:MaximumLengthExceeded").replace(
                    "{0}",
                    "100"
                  )}
                  value={field.value}
                  onChange={(e) => {
                    setValue(
                      "what3Words",
                      e.currentTarget.value ? e.currentTarget.value : undefined,
                      { shouldDirty: true }
                    );
                    trigger("what3Words");
                  }}
                  validationError={
                    errors?.what3Words &&
                    ((field.value?.length as number) > 100
                      ? t("Errors:max_chars_exceeded").replace("{0}", "100")
                      : t("Errors:invalid_what3words_format"))
                  }
                />
              )}
            />
            {!!getValues("latitude") && !!getValues("longitude") && (
              <>
                <Spacer size={16} />
                <span>
                  <img src={MarkerIcon} alt="marker" />
                  <a
                    style={{
                      fontFamily: "Poppins-SemiBold",
                      fontSize: "14px",
                    }}
                    target="_blank"
                    href={getPlacemarkLink()}
                    rel="noreferrer"
                  >
                    {t("Common:labels_placemark")}
                  </a>
                </span>
              </>
            )}

            {!config?.show.includes("ShowObjectDetailsVariant1") && (
              <>
                <Spacer size={16} />

                <Row align="start">
                  <Controller
                    name="doorCode"
                    control={control}
                    render={({ field }) => (
                      <TextInput
                        label={t("Common:door_code")}
                        value={field.value}
                        onChange={field.onChange}
                        placeholder={t("Common:door_placeholder")}
                      />
                    )}
                  />
                  <Spacer size={32} />
                  <Controller
                    name="alarmCode"
                    control={control}
                    rules={{ maxLength: 60 }}
                    render={({ field }) => (
                      <TextInput
                        inputRef={field.ref}
                        label={t("Objects:address_input_alarm_code")}
                        value={field.value}
                        onChange={field.onChange}
                        placeholder={t(
                          "Transmitters:create_schedule_placeholder_alarmcode"
                        )}
                        validationError={
                          errors?.alarmCode &&
                          t("Errors:max_chars_exceeded").replace("{0}", "60")
                        }
                      />
                    )}
                  />
                </Row>
              </>
            )}

            <Spacer size={16} />

            <Row align="start">
              <Controller
                name="telephone1"
                control={control}
                rules={{
                  required: !watch("streetAddress"),
                  validate: (value) => {
                    if (value?.length <= 5) {
                      return validateEmergencyPhoneNumberIfExists(value);
                    }
                    return validatePhoneNumberIfExists(value);
                  },
                }}
                render={({ field }) => (
                  <TextInput
                    inputRef={field.ref}
                    required={!watch("streetAddress")}
                    label={t("Common:address_main_number")}
                    value={field.value}
                    onChange={field.onChange}
                    description={t("Objects:address_input_phone_description")}
                    validationError={getErrorMessage(errors.telephone1)}
                    placeholder={t("Common:input_phone_description")}
                  />
                )}
              />
              <Spacer size={32} />
              <Controller
                name="telephone2"
                control={control}
                rules={{
                  validate: (value) => {
                    if (value?.length <= 5) {
                      return validateEmergencyPhoneNumberIfExists(value);
                    }
                    return validatePhoneNumberIfExists(value);
                  },
                }}
                render={({ field }) => (
                  <TextInput
                    inputRef={field.ref}
                    label={t("Common:address_secondary_number")}
                    value={field.value}
                    onChange={(e) => {
                      field.onChange(e.currentTarget.value);
                      trigger("streetAddress");
                    }}
                    placeholder={t("Common:input_phone_description")}
                    validationError={getErrorMessage(errors.telephone2)}
                  />
                )}
              />
            </Row>
            <Spacer size={16} />
            <Controller
              name="customerSpecific3"
              control={control}
              render={({ field }) => (
                <Autocomplete
                  name={field.name}
                  fieldData={autocompleteData?.customerSpecific3}
                  defaultLabel={t("Objects:customer_specific_3")}
                  defaultValidation={{ maxLength: 200 }}
                />
              )}
            />
            <Spacer size={32} />
          </form>
        </FormProvider>
      )}
    </PrimeModal>
  );
};

export default observer(AddressForm);
